import { Link } from "react-router";
import type { DuplicateMatch } from "@/domain/duplicates";
import type { Expense } from "@/domain/model";
import { formatDate, formatEurPlain } from "@/lib/format";
import { DUPLICATE_LEVEL_LABELS, DUPLICATE_REASON_LABELS } from "@/lib/labels";
import { Badge } from "./primitives";

export interface MatchRow {
  match?: DuplicateMatch;
  expense: Expense;
}

/** The existing transactions a new or saved expense resembles. Read-only: nothing here edits them. */
export function DuplicateMatches({
  rows,
  categoryLabel,
  methodLabel,
  linkTarget,
}: {
  rows: MatchRow[];
  categoryLabel: (id: string) => string;
  methodLabel: (id: string | null) => string;
  /** Open matches in a new tab so an unsaved form is not lost. */
  linkTarget?: "_blank";
}) {
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
      {rows.map(({ match, expense }) => (
        <li key={expense.id} className="px-3 py-2.5 text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/spese/${expense.id}`}
                target={linkTarget}
                rel={linkTarget ? "noopener" : undefined}
                className="font-medium underline decoration-line underline-offset-4 hover:text-petrol"
              >
                {expense.merchant ?? categoryLabel(expense.categoryId)}
              </Link>
              <p className="text-muted">
                {formatDate(expense.purchaseDate)}, {methodLabel(expense.paymentMethodId)}
                {expense.merchant ? `, ${categoryLabel(expense.categoryId)}` : ""}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="amount font-semibold">{formatEurPlain(expense.amountEur)}</p>
              {match ? <Badge tone={match.level === "likely" ? "warn" : "neutral"}>{DUPLICATE_LEVEL_LABELS[match.level]}</Badge> : null}
            </div>
          </div>
          {match ? <p className="mt-1 text-xs text-muted">{match.reasons.map((r) => DUPLICATE_REASON_LABELS[r]).join(", ")}</p> : null}
        </li>
      ))}
    </ul>
  );
}
